import { useState } from 'react';

interface PurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (packs: number) => Promise<void>;
  isLoading?: boolean;
}

const PRICE_PER_PACK = 0.1;
const CREDITS_PER_PACK = 5;

export default function PurchaseModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}: PurchaseModalProps) {
  const [selectedPacks, setSelectedPacks] = useState(1);

  if (!isOpen) return null;

  const totalSol = selectedPacks * PRICE_PER_PACK;
  const totalCredits = selectedPacks * CREDITS_PER_PACK;

  const handleConfirm = async () => {
    await onConfirm(selectedPacks);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-fade-in"
        onClick={!isLoading ? onClose : undefined}
      />

      {/* Modal */}
      <div className="relative w-full max-w-sm mx-4 animate-scale-in">
        {/* Glass panel */}
        <div className="relative bg-[#0a0a0a]/95 border border-white/[0.08] rounded-xl overflow-hidden">
          {/* Subtle top accent line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

          {/* Content */}
          <div className="p-6">
            {/* Header */}
            <div className="text-center mb-6">
              <h2 className="font-display text-2xl font-bold tracking-wide text-white mb-2">
                Buy Run Credits
              </h2>
              <p className="text-white/40 text-xs tracking-[0.15em] uppercase">
                Select your pack size
              </p>
            </div>

            {/* Pack selection */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {[1, 2].map((packs) => {
                const isSelected = selectedPacks === packs;
                const packCredits = packs * CREDITS_PER_PACK;
                const packPrice = packs * PRICE_PER_PACK;

                return (
                  <button
                    key={packs}
                    onClick={() => setSelectedPacks(packs)}
                    disabled={isLoading}
                    className={`relative p-4 rounded-lg border transition-all duration-200 ${
                      isSelected
                        ? 'bg-white/[0.06] border-white/20'
                        : 'bg-white/[0.02] border-white/[0.06] hover:border-white/10'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#22c55e]" />
                    )}

                    {/* Pack info */}
                    <div className="text-left">
                      <div className="font-mono text-2xl font-bold text-white mb-1">
                        {packCredits}
                      </div>
                      <div className="text-white/40 text-[10px] tracking-[0.2em] uppercase mb-3">
                        {packs === 1 ? '1 Pack' : '2 Packs'}
                      </div>
                      <div className="font-mono text-sm text-white/60">
                        {packPrice.toFixed(1)} SOL
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Price summary */}
            <div className="bg-white/[0.03] rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-white/40 text-xs tracking-[0.1em] uppercase">
                  Total Credits
                </span>
                <span className="font-mono text-lg font-bold text-white">
                  {totalCredits}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/40 text-xs tracking-[0.1em] uppercase">
                  Total Cost
                </span>
                <span className="font-mono text-lg font-bold text-white">
                  {totalSol.toFixed(1)} SOL
                </span>
              </div>
            </div>

            {/* Expiry warning */}
            <div className="flex items-center gap-2 px-3 py-2 bg-[#f59e0b]/10 border border-[#f59e0b]/20 rounded-lg mb-6">
              <svg
                className="w-4 h-4 text-[#f59e0b] flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-[#f59e0b]/80 text-[11px] tracking-wide">
                Credits expire at 00:00 UTC daily
              </span>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 px-4 py-3 border border-white/10 text-white/60 text-sm font-semibold tracking-[0.1em] uppercase rounded-lg
                         hover:border-white/20 hover:text-white/80 transition-all
                         disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={isLoading}
                className="flex-1 px-4 py-3 bg-white text-black text-sm font-bold tracking-[0.1em] uppercase rounded-lg
                         hover:bg-white/90 transition-all
                         disabled:opacity-70 disabled:cursor-not-allowed
                         flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                    <span>Processing</span>
                  </>
                ) : (
                  <span>Confirm</span>
                )}
              </button>
            </div>
          </div>

          {/* Bottom accent */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>
      </div>
    </div>
  );
}
